import * as React from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';
import { fr } from 'date-fns/locale';
import { VariantProps } from 'class-variance-authority';

import { cn } from '@/packs/ui/lib/utils';
import { buttonVariants } from '@/packs/ui/components/ui/button';
import './calendar.css';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  buttonVariant = 'ghost',
  formatters,
  components,
  locale = fr,
  startMonth,
  endMonth,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: VariantProps<typeof buttonVariants>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();

  // react-day-picker v9 bounds the dropdown range with startMonth/endMonth.
  // v8's fromYear/toYear were removed and are silently ignored by v9.
  const currentYear = new Date().getFullYear();
  const resolvedStartMonth = startMonth ?? new Date(currentYear - 150, 0);
  const resolvedEndMonth = endMonth ?? new Date(currentYear + 10, 11);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      startMonth={resolvedStartMonth}
      endMonth={resolvedEndMonth}
      className={cn(
        'bg-background group/calendar',
        'p-2 [--cell-size:1.75rem] [--rdp-day-height:34px] [--rdp-day-width:34px] [--rdp-day_button-height:32px] [--rdp-day_button-width:32px] [[data-slot=card-content]_&]:bg-transparent',
        '[[data-slot=popover-content]_&]:bg-transparent',
        'rounded-lg [&_.rdp-month]:m-0 [&_.rdp-month]:gap-0 [&_.rdp-nav]:h-[44px] [&_.rdp-month>div]:h-[44px]',
        '[&_svg]:text-gray-500',
        '[&_svg]:fill-gray-500',
        '[&_select]:cursor-pointer',
        '[&_option]:cursor-pointer',
        '[&_button]:cursor-pointer',
        '[&_.rdp-today]:text-slate-700',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: date => {
          const month = date.toLocaleString('fr-FR', { month: 'short' });
          return month.charAt(0).toUpperCase() + month.slice(1);
        },
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative gap-4',
          props.mode === 'range' ? '!grid !grid-cols-2' : 'flex flex-col md:flex-row',
          defaultClassNames.months
        ),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-md border',
          defaultClassNames.dropdown_root
        ),
        dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : '[&>svg]:text-slate-500 flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5',
          defaultClassNames.caption_label
        ),
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-slate-500 flex-1 select-none rounded-md text-[0.8rem] font-normal',
          defaultClassNames.weekday
        ),
        week: cn('flex w-full', defaultClassNames.week),
        week_number_header: cn('w-[--cell-size] select-none', defaultClassNames.week_number_header),
        week_number: cn('text-slate-500 select-none text-[0.8rem]', defaultClassNames.week_number),
        day: cn(
          'group/day relative h-[34px] w-[34px] select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day
        ),
        range_start: cn('', defaultClassNames.range_start),
        range_middle: cn('', defaultClassNames.range_middle),
        range_end: cn('', defaultClassNames.range_end),
        today: cn('', defaultClassNames.today),
        outside: cn('text-slate-400 aria-selected:text-slate-400', defaultClassNames.outside),
        disabled: cn('text-slate-400 opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn('rdp-root', className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <svg
                className={cn('rdp-chevron', className)}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                {...props}
              >
                <polygon points="16 18.112 9.81111111 12 16 5.87733333 14.0888889 4 6 12 14.0888889 20" />
              </svg>
            );
          }

          if (orientation === 'right') {
            return (
              <svg
                className={cn('rdp-chevron', className)}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                {...props}
              >
                <polygon points="8 18.112 14.18888889 12 8 5.87733333 9.91111111 4 18 12 9.91111111 20" />
              </svg>
            );
          }

          return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'h-[28px] w-[28px] p-0 m-0 border-0 cursor-pointer text-sm font-normal rounded-md transition-colors',
        'hover:bg-slate-50',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground',
        'data-[range-start=true]:bg-primary data-[range-start=true]:text-white',
        'data-[range-end=true]:bg-primary data-[range-end=true]:text-white',
        'data-[range-middle=true]:bg-primary data-[range-middle=true]:text-white',
        'group-data-[focused=true]/day:ring-2 group-data-[focused=true]/day:ring-ring/50',
        defaultClassNames.day_button,
        className
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
